import { parseSignalingMessage, type ParseOutcome, type SignalingMessage } from './protocol';
import { buildSignalingUrl } from './ws-url';

/**
 * Як саме закрився сигнальний канал — у термінах застосунку, а не кодів.
 *
 * Коди беруться з `main.py`: 4000 «Room full», 1008 (недійсна кімната або
 * зіпсований Host), 1000 «Reconnected» — коли сервер сам закрив цей сокет,
 * бо той самий `client_id` підключився наново.
 */
export type ClosureKind =
  /** Кімната вже має двох учасників. Повторювати підключення безглуздо. */
  | 'room-full'
  /** Сервер вважає кімнату недійсною. Повторювати з тим самим id безглуздо. */
  | 'invalid-room'
  /** Наш сокет замінено іншим із тим самим client_id. Не наша черга відновлювати. */
  | 'replaced'
  /** Усе інше: обрив, таймаут простою, мережа. Підлягає відновленню. */
  | 'transient';

export interface ChannelClosure {
  kind: ClosureKind;
  code: number;
  reason: string;
}

export function classifyClosure(code: number, reason: string): ClosureKind {
  if (code === 4000) return 'room-full';
  if (code === 1008) return 'invalid-room';
  // Сервер закриває старий сокет того самого client_id саме з цією причиною.
  // Інші закриття з 1000 лишаємо відновлюваними: тихо перестати перепідключатись
  // через непередбачене «нормальне» закриття гірше, ніж зайва спроба.
  if (code === 1000 && reason === 'Reconnected') return 'replaced';
  return 'transient';
}

export interface WsChannelHandlers {
  onOpen?: () => void;
  onMessage: (message: SignalingMessage) => void;
  /** Повідомлення, яке не вдалося розібрати або яке нас не стосується. */
  onUnusable?: (outcome: Exclude<ParseOutcome, { status: 'ok' }>) => void;
  onClose: (closure: ChannelClosure) => void;
}

/** Дозволяє підставити фейковий сокет у тестах. */
export type WebSocketFactory = (url: string) => WebSocket;

const defaultFactory: WebSocketFactory = (url) => new WebSocket(url);

/**
 * Сигнальний канал: одне зʼєднання, черга вихідних повідомлень, класифікація
 * закриття. Логіки дзвінка тут немає — канал не знає ні про ролі, ні про SDP.
 */
export class WsChannel {
  private socket: WebSocket | null = null;
  private readonly pending: string[] = [];
  private closedByUs = false;

  constructor(
    private readonly handlers: WsChannelHandlers,
    private readonly factory: WebSocketFactory = defaultFactory,
  ) {}

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(roomId: string, clientId: string): void {
    this.closedByUs = false;
    const socket = this.factory(buildSignalingUrl(roomId, clientId));
    this.socket = socket;

    socket.onopen = (): void => {
      this.flush();
      this.handlers.onOpen?.();
    };

    socket.onmessage = (event: MessageEvent<unknown>): void => {
      if (typeof event.data !== 'string') {
        this.handlers.onUnusable?.({ status: 'invalid', reason: 'бінарний кадр' });
        return;
      }
      const outcome = parseSignalingMessage(event.data);
      if (outcome.status === 'ok') {
        this.handlers.onMessage(outcome.message);
        return;
      }
      // Ані невідомий тип, ані зламана форма не закривають канал: на іншому
      // боці може бути клієнт іншої версії, і його баг не має класти наш дзвінок.
      this.handlers.onUnusable?.(outcome);
    };

    socket.onclose = (event: CloseEvent): void => {
      this.socket = null;
      if (this.closedByUs) return;
      this.handlers.onClose({
        kind: classifyClosure(event.code, event.reason),
        code: event.code,
        reason: event.reason,
      });
    };
  }

  /** Ставить повідомлення в чергу, якщо канал ще не відкрито. */
  send(payload: Record<string, unknown>): void {
    const text = JSON.stringify(payload);
    if (this.isOpen && this.socket) {
      this.socket.send(text);
      return;
    }
    this.pending.push(text);
  }

  /** Закриття з нашого боку: подія onClose назовні не піднімається. */
  close(): void {
    this.closedByUs = true;
    this.pending.length = 0;
    const socket = this.socket;
    this.socket = null;
    try {
      socket?.close(1000, 'Client closed');
    } catch {
      // Закриття вже закритого сокета не має зривати завершення дзвінка.
    }
  }

  private flush(): void {
    if (!this.socket) return;
    while (this.pending.length > 0) {
      const next = this.pending.shift();
      if (next !== undefined) this.socket.send(next);
    }
  }
}
